import { useState } from 'react';
import styled from 'styled-components';

import { noop } from 'lodash';
import { paradigmLightBlue } from '@paradigm/design-system/src/colors';

import SearchIcon from '@paradigm/design-system/src/assets/SearchIcon';
import { useTrackedCallback } from '@paradigm/features/src/analytics';
import { SearchProps, Search, SearchOption } from './Search';
import { Tab } from './Tabs';

interface TabsSearchProps<T extends SearchOption> extends SearchProps<T> {
  maxWidth: string;
  minWidth: string;
}

interface TabOption extends Tab, SearchOption {}

export function TabsSearch({
  maxWidth,
  minWidth,
  onChange,
  ...rest
}: TabsSearchProps<TabOption>) {
  const [open, setOpen] = useState(false);

  const onFocus = () => {
    setOpen(true);
    rest.onFocus?.();
  };

  const onBlur = () => {
    setOpen(false);
    rest.onBlur?.();
  };

  const handleOnChange = useTrackedCallback(
    'search tabs',
    onChange ?? noop,
    undefined,
    true,
  );

  return (
    <Container open={open} maxWidth={maxWidth} minWidth={minWidth}>
      {open ? (
        <Search
          {...rest}
          placeholder="Search tabs..."
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={handleOnChange}
          enableAutoFocus
        />
      ) : (
        <SearchButton onClick={() => setOpen(true)} />
      )}
    </Container>
  );
}

function SearchButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <Button {...props}>
      <SearchIcon
        width={12}
        height={12}
        css={`
          color: ${paradigmLightBlue};
          margin-right: 6px;
        `}
      />
      Search
    </Button>
  );
}

const Container = styled.div<{
  open: boolean;
  maxWidth: string;
  minWidth: string;
}>`
  position: absolute;
  right: 16px;
  top: 0;
  bottom: 0;
  margin: auto;
  width: ${(props) => (props.open ? props.maxWidth : props.minWidth)};
  height: 100%;
  max-height: ${(props) => (props.open ? '80vh' : '100%')};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Button = styled.button`
  width: 80px;
  height: 24px;
  font-size: 12px;
  border-radius: 12px;
  align-self: center;
  padding: 0 12px;
  display: flex;
  border: none;
  justify-self: end;
  align-items: center;
  color: #8492a3;
  background-color: #0f3155;
`;
